/**
 * In-process keyring client.
 *
 * Provides the same API surface as KeyringClient (the SharedWorker-backed
 * client) but runs the keyring core directly in the calling thread. Used
 * by:
 *
 *   - Tests (vitest doesn't ship a SharedWorker)
 *   - Browsers that disabled SharedWorker (some private modes)
 *   - SSR / Node previews
 *
 * The API matches KeyringClient method-for-method, so RemoteSignerIdentity
 * accepts either. Application code only depends on the union type.
 */

import { ed25519 } from '@noble/curves/ed25519';

import type {
  Bytes,
  Context,
  LinkageProof,
  PublicKey,
  Signature,
} from '../identity';
import { handle, subscribeBroadcasts } from './keyring.core';
import type { KeyringRequest, KeyringResponse, StatusResponse } from './keyring.protocol';

export class InprocKeyringClient {
  #lockListeners = new Set<() => void>();
  #broadcastUnsub: () => void;

  constructor() {
    this.#broadcastUnsub = subscribeBroadcasts((msg) => {
      if (msg.kind === 'lock') {
        this.#lockListeners.forEach((l) => l());
      }
    });
  }

  /** Tear down broadcast subscription. Call when discarding the client. */
  dispose(): void {
    this.#broadcastUnsub();
    this.#lockListeners.clear();
  }

  async #call<T extends KeyringResponse>(req: KeyringRequest): Promise<T> {
    const result = await handle(req);
    if (result.kind === 'error') {
      throw new Error(result.message);
    }
    return result as T;
  }

  onLock(fn: () => void): () => void {
    this.#lockListeners.add(fn);
    return () => this.#lockListeners.delete(fn);
  }

  async unlock(seed: string, password: string, autoLockMs?: number) {
    const req: KeyringRequest = autoLockMs !== undefined
      ? { kind: 'unlock', seed, password, autoLockMs }
      : { kind: 'unlock', seed, password };
    return this.#call(req);
  }

  async generate(password: string, autoLockMs?: number) {
    const req: KeyringRequest = autoLockMs !== undefined
      ? { kind: 'generate', password, autoLockMs }
      : { kind: 'generate', password };
    return this.#call(req);
  }

  async lock() {
    return this.#call({ kind: 'lock' });
  }

  async status(): Promise<StatusResponse> {
    const r = await this.#call({ kind: 'status' });
    if (r.kind !== 'status') throw new Error('Unexpected response');
    return r;
  }

  async touch() {
    return this.#call({ kind: 'touch' });
  }

  async sign(payload: Bytes, context: Context): Promise<Signature> {
    const r = await this.#call({ kind: 'sign', payload, context });
    if (r.kind !== 'sign') throw new Error('Unexpected response');
    return r.signature;
  }

  async verify(payload: Bytes, signature: Signature, context: Context, pubkey: PublicKey): Promise<boolean> {
    const r = await this.#call({ kind: 'verify', payload, signature, context, pubkey });
    if (r.kind !== 'verify') throw new Error('Unexpected response');
    return r.ok;
  }

  async pubkey(context: Context): Promise<PublicKey> {
    const r = await this.#call({ kind: 'pubkey', context });
    if (r.kind !== 'pubkey') throw new Error('Unexpected response');
    return r.pubkey;
  }

  async proveLinkage(ctxA: Context, ctxB: Context): Promise<LinkageProof> {
    const r = await this.#call({ kind: 'linkage', ctxA, ctxB });
    if (r.kind !== 'linkage') throw new Error('Unexpected response');
    return { ctxA: r.ctxAHash, ctxB: r.ctxBHash, proof: r.proof, zeroKnowledge: r.zeroKnowledge };
  }

  async storageKey(domain: 'envelope' | 'session' | 'blob'): Promise<CryptoKey> {
    const r = await this.#call({ kind: 'storageKey', domain });
    if (r.kind !== 'storageKey') throw new Error('Unexpected response');
    // .slice() copies into a fresh ArrayBuffer-backed Uint8Array — TS 5.7+
    // rejects SharedArrayBuffer-backed Uint8Arrays for crypto.subtle inputs.
    const key = await crypto.subtle.importKey(
      'raw',
      r.keyBytes.slice(),
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt'],
    );
    r.keyBytes.fill(0);
    return key;
  }

  async exportBackup(password: string): Promise<Uint8Array> {
    const r = await this.#call({ kind: 'exportBackup', password });
    if (r.kind !== 'exportBackup') throw new Error('Unexpected response');
    return r.blob;
  }

  async importBackup(blob: Uint8Array, password: string, autoLockMs?: number) {
    const req: KeyringRequest = autoLockMs !== undefined
      ? { kind: 'importBackup', blob, password, autoLockMs }
      : { kind: 'importBackup', blob, password };
    return this.#call(req);
  }
}

/**
 * Convenience: build a SignerIdentity adapter on top of an InprocKeyringClient.
 *
 * Mirrors RemoteSignerIdentity but uses the inproc client instead of the
 * SharedWorker-backed one. Application code that takes a SignerIdentity can
 * accept either.
 */
export class InprocSignerIdentity {
  #client: InprocKeyringClient;
  #fingerprint: string;

  constructor(client: InprocKeyringClient, fingerprint: string) {
    this.#client = client;
    this.#fingerprint = fingerprint;
  }

  sign(payload: Bytes, context: Context): Promise<Signature> {
    return this.#client.sign(payload, context);
  }

  async verify(payload: Bytes, signature: Signature, _context: Context, pubkey: PublicKey): Promise<boolean> {
    // Local verification — pubkey is public, no secret needed.
    return ed25519.verify(signature, payload, pubkey);
  }

  pubkey(context: Context): Promise<PublicKey> {
    return this.#client.pubkey(context);
  }

  proveLinkage(ctxA: Context, ctxB: Context): Promise<LinkageProof> {
    return this.#client.proveLinkage(ctxA, ctxB);
  }

  async lock(): Promise<void> {
    await this.#client.lock();
  }

  fingerprint(): string {
    return this.#fingerprint;
  }
}
